import { ArrowLeft, ArrowRight, CalendarDate, ChevronDown } from '@untitled-ui/icons-react/build/cjs';
import moment from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ButtonCmp from 'src/components/ButtonCmp';
import SelectBox from 'src/components/SelectBox/SelectBox';
import { TimeRanges } from 'src/constants/common';
import { getDateRange } from 'src/utils/CommonFunctions';

import crossSign from '../../assets/img/cross-select-indicator.svg';

interface IProps {
    handleDatePickerChange: (val: any, type?: string | undefined) => void;
    startDate: any;
    containerClassName?: any;
    parentClassName?: any;
    endDate: any;
    minDate?: any;
    maxDate?: any;
    selectedTimeRange: string;
    allTimeStartDate: any;
    isToday?: boolean;
    isShowDropDownIcon?: boolean;
    isPrevDisabled?: boolean;
    isNextDisabled?: boolean;
    showYearDropdown?: boolean;
    onPrevNextClick?: (type: string) => void;
    previewBoxInnerTitle?: string;
    placeholder?: string;
    textWrapClass?: string;
    calendarClass?: string;
    dateRangeWrapClass?: string;
    containerWrapClassName?: string;
    setTodayAsDefaultOnClear?: boolean;
    arrowLeftClassname?: string;
    arrowRightClassname?: string;
    isClearable?: boolean;
    isOpenDatePicker?: boolean;
    handleDatePickerClose?: any;
    placeholderClassname?: string;
    hideTimeRangeSelect?: boolean;
    datePickerAsModal?: boolean;
}

const DateRangePicker = ({
    handleDatePickerChange,
    onPrevNextClick,
    startDate,
    endDate,
    selectedTimeRange,
    allTimeStartDate,
    containerClassName,
    containerWrapClassName,
    maxDate,
    textWrapClass,
    calendarClass,
    parentClassName,
    isShowDropDownIcon = true,
    showYearDropdown = true,
    isPrevDisabled = false,
    isNextDisabled = false,
    previewBoxInnerTitle = '',
    placeholder,
    dateRangeWrapClass,
    setTodayAsDefaultOnClear = false,
    arrowLeftClassname = '',
    arrowRightClassname = '',
    isClearable = false,
    isOpenDatePicker = false,
    handleDatePickerClose,
    placeholderClassname,
    hideTimeRangeSelect = false,
    datePickerAsModal = false,
}: IProps) => {
    const { t } = useTranslation();
    const uiState = useSelector((state: any) => state.MobileView);
    const appearance = useSelector((state: any) => state.basic.appearance);
    const datePickerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const [dateRange, setDateRange] = useState<any>([]);
    const [startDateCurrent, setStartDateCurrent] = useState<any>(startDate);
    const [endDateCurrent, setEndDateCurrent] = useState<any>(endDate);
    const [timeRangeCurrent, setTimeRangeCurrent] = useState(selectedTimeRange ?? 'custom');
    const [isOpenDropDown, setIsOpenDropDown] = useState<any>(false);
    const [isSelectedStart, setIsSelectedStart] = useState(true);
    const [dropdownPosition, setDropdownPosition] = useState<{
        top: number;
        left: number;
        width: number;
        alignRight: boolean;
    }>({ top: 0, left: 0, width: 0, alignRight: false });
    const [isPositionCalculated, setIsPositionCalculated] = useState(false);

    useEffect(() => {
        setIsOpenDropDown(isOpenDatePicker);

        if (!isOpenDatePicker) {
            setIsPositionCalculated(false);
        }
    }, [isOpenDatePicker]);

    // Calculate dropdown position when it opens so the calendar renders via a
    // fixed-position portal instead of an absolutely-positioned child — otherwise
    // it gets clipped by any ancestor (e.g. table wrappers) with overflow-hidden.
    useEffect(() => {
        if (isOpenDropDown && datePickerRef.current) {
            const updatePosition = () => {
                if (datePickerRef.current && dropdownRef.current) {
                    const rect = datePickerRef.current.getBoundingClientRect();
                    const dropdownWidth = dropdownRef.current.offsetWidth || 800;
                    const viewportWidth = window.innerWidth;

                    const spaceOnRight = viewportWidth - rect.left;
                    const spaceOnLeft = rect.right;
                    const shouldAlignRight = spaceOnRight < dropdownWidth && spaceOnLeft > spaceOnRight;

                    let leftPosition = rect.left + window.scrollX;

                    if (shouldAlignRight) {
                        leftPosition = rect.right + window.scrollX - dropdownWidth;

                        if (leftPosition < 0) {
                            leftPosition = 10;
                        }
                    } else if (leftPosition + dropdownWidth > viewportWidth) {
                        leftPosition = viewportWidth - dropdownWidth - 10;
                    }

                    setDropdownPosition({
                        top: rect.bottom + window.scrollY,
                        left: leftPosition,
                        width: rect.width,
                        alignRight: shouldAlignRight,
                    });
                    setIsPositionCalculated(true);
                }
            };

            setTimeout(updatePosition, 0);

            const handleScroll = () => setIsOpenDropDown(false);
            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', updatePosition);

            return () => {
                window.removeEventListener('scroll', handleScroll, true);
                window.removeEventListener('resize', updatePosition);
            };
        }
    }, [isOpenDropDown]);

    useEffect(() => {
        setStartDateCurrent(startDate);
    }, [startDate]);

    useEffect(() => {
        setEndDateCurrent(endDate);
    }, [endDate]);

    useEffect(() => {
        if (startDate && endDate) {
            setDateRange([startDate, endDate]);
        }
    }, [endDate, startDate]);

    useEffect(() => {
        function handleClickOutside(event: any) {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target) && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpenDropDown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [datePickerRef]);

    const handleTimeRangeChangesCurrent = (val: any) => {
        if (val !== 'custom') {
            const { start, end } = getDateRange(appearance, val, allTimeStartDate);
            setDateRange([start, end]);
            setStartDateCurrent(start);
            setEndDateCurrent(end);
        } else {
            const date = moment().startOf('day').toDate();
            setDateRange([date, date]);
            setStartDateCurrent(date);
            setEndDateCurrent(date);
        }
        setTimeRangeCurrent(val);
    };

    useEffect(() => {
        if (isOpenDropDown) {
            setTimeRangeCurrent(selectedTimeRange);
            setStartDateCurrent(startDate ?? null);
            setEndDateCurrent(endDate ?? null);
            const observer = new MutationObserver(() => {
                const focusedOption = document.querySelector('.select__option--is-focused');

                if (focusedOption) {
                    focusedOption.classList.remove('select__option--is-focused');
                }
            });
            observer.observe(document.body, { childList: true, subtree: true });
        }
    }, [isOpenDropDown, selectedTimeRange]);

    const handleDateChange = (dates: any) => {
        const [start, end] = dates;
        let dateRangeVal: any[] = [];
        setIsSelectedStart(true);

        const updateTimeToCurrent = (date: Date | null) => {
            if (date) {
                const currentDate = new Date();

                // If the time is 00:00:00, set it to the current time
                if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0) {
                    date.setHours(currentDate.getHours());
                    date.setMinutes(currentDate.getMinutes());
                    date.setSeconds(currentDate.getSeconds());
                }

                return date;
            } else {
                return null;
            }
        };

        if (start && end) {
            setStartDateCurrent(updateTimeToCurrent(start)); // Update start date with current time
            setEndDateCurrent(updateTimeToCurrent(end)); // Update end date with current time
            if (dates[1]) setDateRange(dates);
        } else {
            if (!startDateCurrent || endDateCurrent) {
                setStartDateCurrent(updateTimeToCurrent(start));
                setEndDateCurrent(null); // Reset the end date if a new range is being started
                dateRangeVal = [start, null];
            } else if (start < startDateCurrent) {
                // If the selected date is before the start date, make it the new start date
                setEndDateCurrent(startDateCurrent);
                setStartDateCurrent(start);
                dateRangeVal = [start, startDateCurrent];
            } else {
                // Otherwise, set it as the end date
                dateRangeVal = [null, null];

                if (start) {
                    setEndDateCurrent(start);
                    dateRangeVal = [startDateCurrent, start];
                }
            }
            setDateRange(dateRangeVal);
        }

        // Update the custom time range selection
        setTimeRangeCurrent('custom');
    };

    const getFormattedDate = (date: any) => {
        const momentDate = moment(date);
        const today = moment().startOf('day');
        const yesterday = moment().subtract(1, 'days').startOf('day');
        const tomorrow = moment().add(1, 'days').startOf('day');

        if (momentDate.isSame(today, 'day')) {
            return `Today, ${momentDate.format('MMM Do, YYYY')}`;
        } else if (momentDate.isSame(yesterday, 'day')) {
            return `Yesterday, ${momentDate.format('MMM Do, YYYY')}`;
        } else if (momentDate.isSame(tomorrow, 'day')) {
            return `Tomorrow, ${momentDate.format('MMM Do, YYYY')}`;
        } else {
            return momentDate.format('dddd, MMM Do, YYYY');
        }
    };

    const handleCancel = () => {
        setTimeRangeCurrent(selectedTimeRange ?? 'custom');
        setStartDateCurrent(startDate ?? null);
        setEndDateCurrent(endDate ?? null);
        setDateRange([startDate, endDate]);
        setIsOpenDropDown(false);

        if (handleDatePickerClose) {
            handleDatePickerClose();
        }
    };

    const handleApply = () => {
        handleDatePickerChange(dateRange, timeRangeCurrent);
        setIsOpenDropDown(false);
    };

    const handleClear = () => {
        if (setTodayAsDefaultOnClear) {
            handleDateChange([moment().toDate(), moment().toDate()]);
            setDateRange([moment().toDate(), moment().toDate()]);
        } else {
            handleDateChange([]);
            setDateRange([]);
        }
    };

    const handleToday = () => {
        handleDateChange([moment().toDate(), moment().toDate()]);
        setDateRange([moment().toDate(), moment().toDate()]);
    };

    return (
        <div className={`relative w-full cursor-pointer ${parentClassName}`} ref={datePickerRef}>
            <div className={`w-full xl:px-3.5 px-3 py-[7px] rounded-lg shadow border border-gray-300 h-[32px] ${dateRangeWrapClass} ${''} justify-between items-center gap-1.5 flex `}>
                {onPrevNextClick && startDate && (
                    <ArrowLeft
                        className={`xxl:w-4 xxl:h-4 xls:w-3 xls:h-3 w-4 h-4 ${!isPrevDisabled && !startDate ? 'text-grayText cursor-not-allowed' : 'cursor-pointer'} ${arrowLeftClassname}`}
                        onClick={() => onPrevNextClick('prev')}
                    />
                )}
                <div className={`flex flex-1 justify-center items-center ${textWrapClass}`} onClick={() => setIsOpenDropDown(!isOpenDropDown)}>
                    {previewBoxInnerTitle && <div className="text-textSecondary xl:text-xs xls:text-[11px] text-xs font-medium leading-none mr-4">{previewBoxInnerTitle} : </div>}
                    <CalendarDate className={`w-4 h-4 xl:mr-2 mr-1 ${!startDate && !endDate ? 'text-gray-500' : ''} ${calendarClass}`} />
                    <div className="text-textSecondary xl:text-xs xls:text-[11px] text-xs font-medium leading-none">
                        {startDate && endDate ? (
                            selectedTimeRange === 'custom' || selectedTimeRange === 'daily' ? (
                                <>
                                    {/* Calendar dates are timezone-less — format as-is (local), matching what the
                                        calendar highlighted. Converting via appearance.timezone shifted the label a
                                        day back for users whose local timezone is ahead of the org timezone
                                        (e.g. selected Aug 11-21, label showed Aug 10-20). */}
                                    {moment(startDate).format('DD-MM-YYYY') === moment(endDate).format('DD-MM-YYYY')
                                        ? `${getFormattedDate(startDate)}`
                                        : `${moment(startDate).format('MMM Do, YYYY')} -
                                    ${moment(endDate ? endDate : startDate).format('MMM Do, YYYY')}`}
                                </>
                            ) : moment(startDate).format('DD-MM-YYYY') === moment(endDate).format('DD-MM-YYYY') ? (
                                `${getFormattedDate(startDate)}`
                            ) : (
                                <>
                                    {startDate && moment(startDate).format('MMM Do, YYYY')} - {moment(endDate ? endDate : startDate && startDate).format('MMM Do, YYYY')}
                                </>
                            )
                        ) : (
                            <div className={`text-xs text-gray-500 ${placeholderClassname}`}>{placeholder ?? 'No date selected'}</div>
                        )}
                    </div>
                </div>
                {isShowDropDownIcon && <ChevronDown className="w-4 h-4" />}
                {isClearable && startDate && (
                    <div
                        className="date-range-cross-icon"
                        onClick={() => {
                            handleDatePickerChange([null, null], 'custom');
                        }}
                    >
                        <img src={crossSign} className="w-3.5 h-3.5" />
                    </div>
                )}

                {onPrevNextClick && endDate && (
                    <ArrowRight
                        className={`xxl:w-4 xxl:h-4 xls:w-3 xls:h-3 w-4 h-4 ${!isNextDisabled && !endDate ? 'text-grayText cursor-not-allowed' : 'cursor-pointer'} ${arrowRightClassname}`}
                        onClick={() => onPrevNextClick('next')}
                    />
                )}
            </div>
            {isOpenDropDown &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className={`wal-hq-select ${datePickerAsModal ? 'datepicker-as-modal' : ''} ${containerWrapClassName ?? ''}`}
                        style={{
                            position: 'fixed',
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            ...(hideTimeRangeSelect ? {} : { minWidth: `${dropdownPosition.width}px` }),
                            maxWidth: '90vw',
                            zIndex: 9999,
                            opacity: isPositionCalculated ? 1 : 0,
                            visibility: isPositionCalculated ? 'visible' : 'hidden',
                            transition: 'opacity 0.1s ease-in-out',
                        }}
                    >
                        <div
                            className={`${containerClassName} ${
                                datePickerAsModal ? 'datepicker-as-modal-block' : ''
                            } date-picker-menu bg-white border-utilityGray200 border rounded-xl mt-1 flex relative ${hideTimeRangeSelect ? 'shadow-xl' : ''}`}
                        >
                            {hideTimeRangeSelect ? (
                                <div className="hidden" />
                            ) : (
                                <SelectBox
                                    name="timeRangeListGroup"
                                    id="timeRangeListGroup"
                                    className="form_control shadow"
                                    size="sm"
                                    classComp="hide-select-value-container lg:block hidden"
                                    options={TimeRanges}
                                    isCloseOnScroll={false}
                                    inlineSelect={true}
                                    onMenuOpen={() => setIsOpenDropDown(true)}
                                    onChangeFunc={(event: any) => handleTimeRangeChangesCurrent(event.value)}
                                    value={TimeRanges.find((val) => val.value === timeRangeCurrent)}
                                    defaultValue={TimeRanges.find((val) => val.value === timeRangeCurrent)}
                                    menuIsOpen={isOpenDropDown}
                                />
                            )}

                            <div className={`w-full ${hideTimeRangeSelect ? '!ml-0' : 'left-[61%] lg:border-l border-utilityGray200'} custom-time-range flex flex-col`}>
                                <div className="flex-grow relative w-full">
                                    {timeRangeCurrent === 'custom' ? (
                                        <DatePicker
                                            selected={isSelectedStart ? startDateCurrent : endDateCurrent}
                                            className="form_control w-[132px] form_input_control"
                                            onChange={(date: any) => handleDateChange(date)}
                                            startDate={startDateCurrent}
                                            endDate={endDateCurrent}
                                            maxDate={maxDate}
                                            monthsShown={uiState.isMobile ? 1 : 2}
                                            showYearDropdown={showYearDropdown}
                                            selectsRange={true}
                                            inline={isOpenDropDown}
                                        />
                                    ) : (
                                        <DatePicker
                                            selected={startDateCurrent ? moment(startDateCurrent).toDate() : null}
                                            className="form_control w-[132px] form_input_control"
                                            onChange={(date: any) => handleDateChange(date)}
                                            startDate={startDateCurrent ? new Date(moment(startDateCurrent).format('MMM DD, YYYY')) : null}
                                            endDate={endDateCurrent ? moment(endDateCurrent).toDate() : null}
                                            maxDate={maxDate}
                                            monthsShown={uiState.isMobile ? 1 : 2}
                                            showYearDropdown={showYearDropdown}
                                            selectsRange={true}
                                            inline={isOpenDropDown}
                                        />
                                    )}
                                </div>
                                <div className="w-full py-[18px] px-4 border-t border-gray-200 justify-between items-start flex lg:flex-row flex-col gap-3">
                                    <div className="justify-start items-center lg:gap-3 sm:gap-2 gap-1 flex text-xs font-normal leading-tight lg:w-auto w-full">
                                        <ButtonCmp className="lg:flex-auto flex-1 btn_secondary_black" onClick={handleToday}>
                                            {t('Common.Today')}
                                        </ButtonCmp>
                                        <ButtonCmp className="lg:flex-auto flex-1 btn_secondary_black hidden" onClick={handleClear}>
                                            {t('Common.Clear')}
                                        </ButtonCmp>
                                    </div>
                                    <div className="gap-3 flex lg:w-auto w-full">
                                        <ButtonCmp className="lg:flex-auto flex-1 btn_secondary_black" onClick={handleCancel}>
                                            {t('Common.Cancel')}
                                        </ButtonCmp>
                                        <ButtonCmp className="lg:flex-auto flex-1 btn_primary" onClick={handleApply}>
                                            {t('Common.Apply')}
                                        </ButtonCmp>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default DateRangePicker;
